import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { PageHeader } from "@/components/navigation/PageHeader";
import { StepProgress } from "@/components/booking/StepProgress";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Shield, CreditCard, Smartphone, CheckCircle2, AlertCircle, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { invokeFunctionWithAuth } from "@/lib/supabase/functions";
import { loadStripe, StripeElementsOptions } from "@stripe/stripe-js";
import { Elements, PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { PromoCodeSchema, PaymentDataSchema } from "@/lib/validation/schemas";
import { sanitizeError } from "@/lib/security/errorHandler";
import { checkPromoCodeRateLimit } from "@/lib/security/rateLimit";

type OrderSummary = {
  packageName: string;
  basePrice: number;
  sameDayFee: number;
  serviceFee: number;
  subtotal: number;
  discount: number;
  total: number;
  promoCode?: string | null;
};

// Initialize Stripe when key is available
// This prevents the error "Please call Stripe() with your publishable key. You used an empty string."
const stripeKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;
const stripePromise = stripeKey && stripeKey.trim() !== ''
  ? loadStripe(stripeKey)
  : null;

// Internal component for Stripe Payment Form
function StripePaymentForm({ 
  clientSecret, 
  bookingId, 
  orderSummary,
  onSuccess 
}: { 
  clientSecret: string;
  bookingId: string;
  orderSummary: OrderSummary;
  onSuccess: (paymentIntentId: string) => void;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      // Confirm payment with Stripe
      // Note: paymentIntent doesn't exist yet, so we can't reference it in return_url
      // Stripe will automatically add payment_intent to the URL when redirecting
      const { error: confirmError, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/book/payment-success?booking_id=${bookingId}`,
        },
        redirect: 'if_required', // Only redirect if required (3D Secure, etc)
      });

      if (confirmError) {
        // Handle payment errors
        let errorMessage = 'Payment failed';
        
        if (confirmError.type === 'card_error' || confirmError.type === 'validation_error') {
          errorMessage = confirmError.message || 'Payment failed';
          
          // User-friendly error messages
          if (confirmError.code === 'card_declined') {
            errorMessage = 'Your card was declined. Please try another payment method.';
          } else if (confirmError.code === 'insufficient_funds') {
            errorMessage = 'Insufficient funds. Please try another payment method.';
          } else if (confirmError.code === 'expired_card') {
            errorMessage = 'Your card has expired. Please use a different card.';
          } else if (confirmError.code === 'generic_decline') {
            errorMessage = 'Your card was declined. Please contact your bank or try another payment method.';
          }
        }
        
        setError(errorMessage);
        toast.error(errorMessage);
        setIsProcessing(false);
        return;
      }

      // Payment succeeded
      if (paymentIntent && paymentIntent.status === 'succeeded') {
        toast.success('Payment successful!');
        onSuccess(paymentIntent.id);
        // Navigate to success page with payment intent ID
        window.location.href = `/book/payment-success?payment_intent=${paymentIntent.id}&booking_id=${bookingId}`;
      } else if (paymentIntent && paymentIntent.status === 'requires_action') {
        // 3D Secure or other action required
        // Stripe will redirect automatically to return_url when action is completed
        // Stripe will add payment_intent parameter to the URL automatically
        console.log('Payment requires additional action (3D Secure, etc.) - Stripe will redirect automatically');
        // Don't set isProcessing to false - Stripe will handle redirect
      } else if (paymentIntent) {
        // Payment is in another state (processing, etc.)
        console.log('Payment status:', paymentIntent.status);
        setError(`Payment status: ${paymentIntent.status}. Please wait...`);
        setIsProcessing(false);
      } else {
        // No payment intent returned (should not happen, but handle gracefully)
        console.error('No payment intent returned from confirmPayment');
        setError('Payment confirmation completed but no payment intent was returned. Please check your booking status.');
        setIsProcessing(false);
      }
    } catch (err: any) {
      console.error('Payment error:', err);
      const errorMessage = err.message || 'An unexpected error occurred. Please try again.';
      setError(errorMessage);
      toast.error(errorMessage);
      setIsProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <Card className="mt-6">
        <CardContent className="p-6 space-y-4">
          <h3 className="font-semibold mb-4">Payment Details</h3>
          <div className="space-y-4">
            <PaymentElement 
              options={{
                layout: 'tabs',
                wallets: {
                  applePay: 'auto',
                  googlePay: 'auto',
                },
              }}
            />
          </div>
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      <Button
        type="submit"
        disabled={!stripe || isProcessing || !orderSummary}
        className="w-full h-12 text-base font-semibold"
        size="lg"
      >
        {isProcessing ? 'Processing...' : orderSummary ? `Pay $${orderSummary.total.toFixed(2)}` : 'Loading...'}
      </Button>
    </form>
  );
}

export default function BookPayment() {
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  // Debug: Log environment variables on mount
  useEffect(() => {
    if (!stripeKey) {
      console.warn('⚠️ WARNING: VITE_STRIPE_PUBLISHABLE_KEY is not set! Stripe payments will not work.');
    }
  }, []);
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Stripe Elements state (for live mode)
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [paymentIntentId, setPaymentIntentId] = useState<string | null>(null);
  const [bookingId, setBookingId] = useState<string | null>(null);
  const [loadingStripe, setLoadingStripe] = useState(false);
  
  // Card form state
  const [cardNumber, setCardNumber] = useState('');
  const [cardholderName, setCardholderName] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [cvv, setCvv] = useState('');
  const [sameAsService, setSameAsService] = useState(true);
  const [address, setAddress] = useState('');
  const [apartment, setApartment] = useState('');
  const [city, setCity] = useState('');
  const [postalCode, setPostalCode] = useState('');

  // Order summary state - initialize with null/empty to force loading from package
  const [orderSummary, setOrderSummary] = useState<OrderSummary | null>(null);
  const [loadingPackage, setLoadingPackage] = useState(true);
  const [bookingFormData, setBookingFormData] = useState<any | null>(null);

  const [promoOpen, setPromoOpen] = useState(true);
  const [promoCodeInput, setPromoCodeInput] = useState("");
  const [appliedPromo, setAppliedPromo] = useState<{ code: string; discount: number } | null>(null);
  const [isValidatingPromo, setIsValidatingPromo] = useState(false);
  const [promoError, setPromoError] = useState<string | null>(null);
  const [promoMessage, setPromoMessage] = useState<string | null>(null);
  const STANDARD_LAUNCH_CODE = (import.meta.env.VITE_STANDARD_LAUNCH_CODE || '').toUpperCase();
  const [includeStandardCode, setIncludeStandardCode] = useState(false);

  const syncBookingData = (updates: Record<string, any>) => {
    try {
      const formDataStr = sessionStorage.getItem('bookingFormData');
      if (formDataStr) {
        const stored = JSON.parse(formDataStr);
        const updated = { ...stored, ...updates };
        sessionStorage.setItem('bookingFormData', JSON.stringify(updated));
      }
    } catch (error) {
      console.warn('Failed to sync booking session data:', error);
    }
  };

  const applyBookingDataUpdates = (updates: Record<string, any>) => {
    setBookingFormData((prev) => {
      if (prev) {
        return { ...prev, ...updates };
      }
      try {
        const formDataStr = sessionStorage.getItem('bookingFormData');
        if (formDataStr) {
          const stored = JSON.parse(formDataStr);
          return { ...stored, ...updates };
        }
      } catch (error) {
        console.warn('Failed to parse booking data while applying updates:', error);
      }
      return { ...updates };
    });
    syncBookingData(updates);
  };

  const resetStripeState = useCallback(() => {
    setClientSecret(null);
    setPaymentIntentId(null);
    setBookingId(null);
  }, []);

  const applyPromoToSummary = useCallback((code: string | null, discountCents: number) => {
    setOrderSummary((prev) => {
      if (!prev) return prev;
      const discount = Math.max(discountCents, 0) / 100;
      const total = Math.max(prev.subtotal - discount, 0);
      return {
        ...prev,
        discount: Math.round(discount * 100) / 100,
        total: Math.round(total * 100) / 100,
        promoCode: code,
      };
    });
  }, []);

  const handleRemovePromo = useCallback(
    (options?: { keepMessages?: boolean }) => {
      setAppliedPromo(null);
      setIncludeStandardCode(false);
      if (!options?.keepMessages) {
        setPromoError(null);
        setPromoMessage(null);
      }
      applyPromoToSummary(null, 0);
      applyBookingDataUpdates({ promoCode: null, promoDiscountCents: 0 });
      resetStripeState();
    },
    [applyPromoToSummary, applyBookingDataUpdates, resetStripeState]
  );

  const handleStandardCodeToggle = useCallback((checked: boolean) => {
    if (!checked) {
      setIncludeStandardCode(false);
      handleRemovePromo();
      setPromoCodeInput('');
      return;
    }

    if (!STANDARD_LAUNCH_CODE) {
      toast.info('Standard launch code is not configured.');
      setIncludeStandardCode(false);
      return;
    }

    setIncludeStandardCode(true);
    setPromoCodeInput(STANDARD_LAUNCH_CODE);
    setAppliedPromo(null);
    setPromoError(null);
    setPromoMessage(null);
  }, [STANDARD_LAUNCH_CODE, handleRemovePromo]);

  const handleValidatePromo = useCallback(async (codeOverride?: string) => {
    if (!orderSummary) {
      toast.error('Order summary is not available yet.');
      return;
    }
    if (!bookingFormData?.selectedPackageId) {
      toast.error('Package not found to apply the promo code.');
      return;
    }

    const codeToValidate = (codeOverride ?? promoCodeInput).trim();

    if (!codeToValidate) {
      setPromoError('Please enter a promo code.');
      handleRemovePromo();
      return;
    }

    // Rate limiting check
    if (!checkPromoCodeRateLimit()) {
      toast.error('Too many validation attempts. Please wait a moment.');
      return;
    }

    // Validate and sanitize promo code with Zod
    try {
      const validatedCode = PromoCodeSchema.parse(codeToValidate);
      const normalizedCode = validatedCode;

      setIsValidatingPromo(true);
      setPromoError(null);
      setPromoMessage(null);

      try {
        const requestBody: Record<string, any> = {
          promoCode: normalizedCode,
          packageId: bookingFormData.selectedPackageId,
          nowFlag: bookingFormData.nowFlag || false,
        };

        if (bookingFormData.phonePrefix && bookingFormData.phone) {
          requestBody.phone = `${bookingFormData.phonePrefix}${bookingFormData.phone}`;
        }

        const { data, error } = await invokeFunctionWithAuth('validate-promo-code', requestBody);

        if (error) {
          throw new Error(error.message || 'Unable to validate the promo code.');
        }

        if (!data?.valid) {
          setPromoError(data?.reason || 'Promo code is invalid or unavailable.');
          handleRemovePromo({ keepMessages: true });
          return;
        }

        const discountCents = Number(data.discountCents) || 0;
        const appliedCode = data.promoCode ? String(data.promoCode).toUpperCase() : normalizedCode;
        applyPromoToSummary(appliedCode, discountCents);
        setAppliedPromo({
          code: appliedCode,
          discount: discountCents / 100,
        });
        setPromoCodeInput(appliedCode);
        setPromoMessage('Promo code applied successfully!');
        applyBookingDataUpdates({
          promoCode: appliedCode,
          promoDiscountCents: discountCents,
        });
        resetStripeState();
      } catch (err: any) {
        console.error('Error validating promo code:', err);
        const errorMessage = sanitizeError(err);
        setPromoError(errorMessage);
        handleRemovePromo();
      } finally {
        setIsValidatingPromo(false);
      }
    } catch (validationError: any) {
      const errorMessage = sanitizeError(validationError);
      setPromoError(errorMessage);
      setIsValidatingPromo(false);
    }
  }, [orderSummary, bookingFormData, promoCodeInput, applyPromoToSummary, applyBookingDataUpdates, resetStripeState, handleRemovePromo]);

  useEffect(() => {
    if (includeStandardCode) {
      if (STANDARD_LAUNCH_CODE) {
        setPromoCodeInput(STANDARD_LAUNCH_CODE);
      } else {
        setIncludeStandardCode(false);
      }
    }
  }, [includeStandardCode, STANDARD_LAUNCH_CODE]);

  const formatCardNumber = (value: string) => {
    const digits = value.replace(/\D/g, '');
    const groups = digits.match(/.{1,4}/g);
    return groups ? groups.join(' ') : digits;
  };

  const formatExpiry = (value: string) => {
    const digits = value.replace(/\D/g, '');
    if (digits.length >= 2) {
      return digits.slice(0, 2) + '/' + digits.slice(2, 4);
    }
    return digits;
  };

  const handleCardNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatCardNumber(e.target.value);
    if (formatted.replace(/\s/g, '').length <= 16) {
      setCardNumber(formatted);
    }
  };

  const handleExpiryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatExpiry(e.target.value);
    if (formatted.replace('/', '').length <= 4) {
      setExpiryDate(formatted);
    }
  };

  const handleCvvChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const digits = e.target.value.replace(/\D/g, '');
    if (digits.length <= 3) {
      setCvv(digits);
    }
  };

  // Load package data from sessionStorage or database
  useEffect(() => {
    const loadPackageData = async () => {
      try {
        setLoadingPackage(true);
        const formDataStr = sessionStorage.getItem('bookingFormData');
        if (!formDataStr) {
          console.warn('No booking data found in sessionStorage');
          setLoadingPackage(false);
          return;
        }

        const formData = JSON.parse(formDataStr);
        setBookingFormData(formData);
        const packageId = formData.selectedPackageId;
        const storedCode = formData.promoCode ? String(formData.promoCode).toUpperCase() : null;
        const storedDiscountCents = Number(formData.promoDiscountCents) || 0;

        if (!packageId) {
          console.warn('No package ID found in form data');
          setLoadingPackage(false);
          return;
        }

        // Fetch package data from database
        const { data: packageData, error } = await supabase
          .from('packages')
          .select('*')
          .eq('id', packageId)
          .single();

        if (error) {
          console.error('Error fetching package:', error);
          toast.error('Failed to load package data');
          setLoadingPackage(false);
          return;
        }

        if (!packageData) {
          console.error('Package not found');
          toast.error('Package not found');
          setLoadingPackage(false);
          return;
        }

        // Calculate totals based on package data and nowFlag
        const basePrice = packageData.base_price_cents / 100;
        const nowFlag = formData.nowFlag || false;
        const sameDayFee = nowFlag ? (packageData.same_day_fee_cents / 100) : 0;
        const serviceFee = packageData.service_fee_cents / 100;
        const subtotal = basePrice + sameDayFee + serviceFee;
        const roundedSubtotal = Math.round(subtotal * 100) / 100;
        const discountValue = storedCode && storedDiscountCents > 0 ? storedDiscountCents / 100 : 0;
        const total = Math.max(roundedSubtotal - discountValue, 0);

        setOrderSummary({
          packageName: packageData.name,
          basePrice,
          sameDayFee,
          serviceFee,
          subtotal: roundedSubtotal,
          discount: Math.round(discountValue * 100) / 100,
          total: Math.round(total * 100) / 100, // Round to 2 decimal places
          promoCode: discountValue > 0 ? storedCode : null
        });

        if (storedCode) {
          setPromoCodeInput(storedCode);
          setIncludeStandardCode(Boolean(STANDARD_LAUNCH_CODE) && storedCode === STANDARD_LAUNCH_CODE);
        } else {
          setPromoCodeInput('');
          setIncludeStandardCode(false);
        }
      } catch (error) {
        console.error('Error loading package data:', error);
        toast.error('Error loading package data');
      } finally {
        setLoadingPackage(false);
      }
    };

    loadPackageData();
  }, []);

  // REMOVED: No longer creating Payment Intent automatically
  // Booking and Payment Intent will be created only when user clicks "Complete Booking"

  const handleSubmit = async () => {
    // Prevent double submission - CRITICAL: This prevents duplicate bookings
    if (isSubmitting) {
      console.warn('⚠️ handleSubmit called while already submitting - ignoring duplicate call');
      return;
    }

    // Additional check: if we already have a bookingId and clientSecret, don't create again
    if (bookingId && clientSecret && stripePromise) {
      // Booking already created - ignoring duplicate submission
      return;
    }

    // handleSubmit called
    
    // Stripe payment mode - handle payment via Stripe
    // Always use Stripe for payments
    if (stripePromise) {
      setIsSubmitting(true);
      console.log('🚀 Creating booking with promo code applied');
      
      try {
        const formDataStr = sessionStorage.getItem('bookingFormData');
        if (!formDataStr) {
          toast.error('Booking data not found');
          setIsSubmitting(false);
          return;
        }

        const formData = JSON.parse(formDataStr);

        if (!orderSummary) {
          toast.error('Package data not loaded. Please refresh the page.');
          setIsSubmitting(false);
          return;
        }

        const activePromoCode = orderSummary.discount > 0 ? orderSummary.promoCode : null;
        
        // Check if amount is less than $0.50 (Stripe minimum)
        // If so, treat as zero (will create booking with zero amount)
        const isAmountBelowMinimum = orderSummary.total > 0 && orderSummary.total < 0.50;
        const requestAmount = isAmountBelowMinimum ? 0 : (orderSummary.total > 0 ? orderSummary.total : 0.01);
        const shouldCreatePaymentIntent = !isAmountBelowMinimum && orderSummary.total > 0;

        // Validate payment data with Zod
        let validatedPromoCode: string | null = null;
        if (activePromoCode) {
          try {
            validatedPromoCode = PromoCodeSchema.parse(activePromoCode);
          } catch (promoError) {
            console.warn('Invalid promo code format, proceeding without promo:', promoError);
            validatedPromoCode = null;
          }
        }

        // Validate amount (bookingId will be created by server, so we don't validate it here)
        const amountInCents = Math.round(requestAmount * 100);
        if (amountInCents < 0) {
          throw new Error('Amount cannot be negative');
        }

        // Create booking with promo code already applied
        const { data, error } = await invokeFunctionWithAuth('create-booking-payment', {
          amount: requestAmount,
          skipPayment: orderSummary.total === 0 || isAmountBelowMinimum, // Mark as paid immediately if total is 0 or below minimum
          promoCode: validatedPromoCode,
          bookingData: {
            packageId: formData.selectedPackageId,
            packageName: orderSummary.packageName,
            fullName: formData.fullName,
            phone: `${formData.phonePrefix}${formData.phone}`,
            email: formData.email || '',
            addressLine1: formData.addressLine1,
            addressLine2: formData.addressLine2 || '',
            city: formData.city,
            postalCode: formData.postalCode,
            lat: formData.lat,
            lng: formData.lng,
            scheduledAt: formData.nowFlag ? null : `${formData.date}T${formData.time.hour.toString().padStart(2, '0')}:${formData.time.minute.toString().padStart(2, '0')}:00`,
            nowFlag: formData.nowFlag,
            notes: formData.notes || '',
          },
        });

        if (error) {
          console.error('Error creating booking:', error);
          const errorMessage = sanitizeError(error);
          toast.error(errorMessage);
          setIsSubmitting(false);
          return;
        }

        // If total is 0 or below minimum, booking is already paid - navigate to confirmation
        if ((orderSummary.total === 0 || isAmountBelowMinimum) && data?.success && data?.bookingId) {
          toast.success('Booking completed successfully!');
          setIsSubmitting(false);
          setTimeout(() => {
            navigate(`/book/confirmation?booking_id=${data.bookingId}`, { replace: true });
          }, 400);
          return;
        }

        // If total > 0 and >= $0.50, set up Stripe Payment Intent
        if (shouldCreatePaymentIntent) {
          if (data?.clientSecret && data?.bookingId) {
            setClientSecret(data.clientSecret);
            setBookingId(data.bookingId);
            if (data.paymentIntentId) {
              setPaymentIntentId(data.paymentIntentId);
            }
            setIsSubmitting(false); // Reset submitting state so user can interact with Stripe form
            // Payment Intent is ready - StripePaymentForm will handle the payment
            toast.success('Payment form ready');
          } else {
            setIsSubmitting(false);
            throw new Error('Payment Intent not created');
          }
        } else if (data?.bookingId) {
          // Fallback: booking created but no payment needed
          toast.success('Booking completed successfully!');
          setIsSubmitting(false);
          setTimeout(() => {
            navigate(`/book/confirmation?booking_id=${data.bookingId}`, { replace: true });
          }, 400);
        } else {
          setIsSubmitting(false);
          throw new Error('Booking ID not received from server');
        }
      } catch (error: any) {
        console.error('Error creating booking:', error);
        const errorMessage = sanitizeError(error);
        toast.error(errorMessage);
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  // Stripe Elements options
  const elementsOptions: StripeElementsOptions = {
    clientSecret,
    appearance: {
      theme: 'stripe' as const,
    },
    locale: 'en',
  };

  return (
    <div className="min-h-screen bg-[#F9FAFB]">
      <PageHeader variant="back" title="Payment" backTo="/book/verify" />
      
      <div className="px-4 py-6">
        <StepProgress currentStep={3} stepLabel="Payment" />

        {/* Order Summary */}
        <Card className="mt-6">
          <CardContent className="p-6">
            <h2 className="text-xl font-semibold mb-4">Order Summary</h2>
            {loadingPackage ? (
              <div className="space-y-3">
                <div className="h-4 bg-muted animate-pulse rounded"></div>
                <div className="h-4 bg-muted animate-pulse rounded"></div>
                <div className="h-4 bg-muted animate-pulse rounded"></div>
                <div className="h-6 bg-muted animate-pulse rounded mt-3"></div>
              </div>
            ) : orderSummary ? (
              <div className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">{orderSummary.packageName}</span>
                  <span className="font-medium">${orderSummary.basePrice.toFixed(2)}</span>
                </div>
                {orderSummary.sameDayFee > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Same-Day Fee</span>
                    <span className="font-medium">${orderSummary.sameDayFee.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Service Fee</span>
                  <span className="font-medium">${orderSummary.serviceFee.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm pt-1">
                  <span className="text-muted-foreground">Subtotal</span>
                  <span className="font-medium">${orderSummary.subtotal.toFixed(2)}</span>
                </div>
                {orderSummary.discount > 0 && (
                  <div className="flex items-center justify-between text-sm text-emerald-600">
                    <span className="font-medium">Promo ({orderSummary.promoCode})</span>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold">- ${orderSummary.discount.toFixed(2)}</span>
                      <button
                        type="button"
                        aria-label="Remove promo code"
                        onClick={() => {
                          handleRemovePromo();
                          setPromoCodeInput('');
                        }}
                        className="text-xs font-semibold text-red-500 transition-colors hover:text-red-600"
                      >
                        ×
                      </button>
                    </div>
                  </div>
                )}
                <div className="border-t pt-3 flex justify-between items-baseline">
                  <span className="font-semibold text-base">Total</span>
                  <div className="text-right">
                    <span className="font-bold text-xl">${orderSummary.total.toFixed(2)}</span>
                    {orderSummary.discount > 0 && (
                      <p className="text-xs text-muted-foreground">
                        Total savings of ${orderSummary.discount.toFixed(2)}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center text-muted-foreground py-4">
                No package data available
              </div>
            )}
          </CardContent>
        </Card>

        {/* Promo Code Collapsible */}
        <Collapsible open={promoOpen} onOpenChange={setPromoOpen}>
          <div className="mt-6">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-foreground">Promo Code</h3>
              <CollapsibleTrigger asChild>
                <button
                  type="button"
                  className="flex items-center gap-2 text-sm font-medium text-foreground transition-colors hover:text-foreground/80"
                  aria-label={promoOpen ? 'Collapse promo code section' : 'Expand promo code section'}
                >
                  <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${promoOpen ? 'rotate-180' : ''}`} />
                </button>
              </CollapsibleTrigger>
            </div>
            <CollapsibleContent>
              <div className="space-y-4 pt-4">
                <div className="space-y-2">
                  <Label htmlFor="promoCode" className="text-sm font-medium">Code</Label>
                  <div className="relative">
                    <Input
                      id="promoCode"
                      value={promoCodeInput}
                      onChange={(e) => setPromoCodeInput(e.target.value)}
                      placeholder="Type Your Code Here"
                      className="h-12 pl-10 text-sm"
                    />
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground"
                      aria-hidden="true"
                    >
                      <path
                        d="M13 21.001a1 1 0 0 1-.707-.293l-7.999-7.999a1 1 0 0 1-.293-.707V3.001a1 1 0 0 1 1-1h9c.266 0 .52.105.707.293l8 8a1 1 0 0 1 0 1.414l-9 8a.998.998 0 0 1-.708.293Zm-7-9.706 7 7 7.585-6.742-7-7.001H6v6.743Zm2.5-3.294a1.5 1.5 0 1 1 2.122 2.122 1.5 1.5 0 0 1-2.122-2.122Z"
                        fill="currentColor"
                      />
                    </svg>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <Checkbox
                    id="standardCode"
                    checked={includeStandardCode}
                    onCheckedChange={(checked) => handleStandardCodeToggle(checked === true)}
                    disabled={!STANDARD_LAUNCH_CODE}
                  />
                  <Label htmlFor="standardCode" className="text-sm text-muted-foreground font-normal">
                    Add Standard Launch Code
                  </Label>
                </div>

                {promoMessage && (
                  <p className="text-sm font-medium text-emerald-600">{promoMessage}</p>
                )}
                {promoError && (
                  <p className="text-sm font-medium text-destructive">{promoError}</p>
                )}

                <Button
                  type="button"
                  onClick={() => void handleValidatePromo()}
                  disabled={isValidatingPromo || !promoCodeInput}
                  variant="outline"
                  className="h-12 w-full rounded-xl border border-muted-foreground/30 bg-white text-sm font-semibold text-foreground shadow-sm hover:bg-[#f53100] hover:text-white focus:bg-[#f53100] focus:text-white active:bg-[#f53100] active:text-white"
                >
                  {isValidatingPromo ? 'Validating...' : 'Validate Code'}
                </Button>

              </div>
            </CollapsibleContent>
          </div>
        </Collapsible>

        <div className="mt-8 border-t border-[#C4C7CC] pt-8" />

        {/* Stripe Payment Mode */}
        {(orderSummary?.total === 0 || (orderSummary && orderSummary.total > 0 && orderSummary.total < 0.50)) ? (
              <>
                <Card className="mt-6 border-dashed">
                  <CardContent className="p-6 text-center space-y-2">
                    <h3 className="font-semibold text-lg">No payment required</h3>
                    <p className="text-sm text-muted-foreground">
                      {orderSummary?.total === 0 
                        ? 'Your promo code covered the entire amount. Click the button below to complete your booking.'
                        : 'The amount is below the minimum payment threshold. Click the button below to complete your booking.'}
                    </p>
                  </CardContent>
                </Card>
                <Button
                  onClick={handleSubmit}
                  disabled={isSubmitting || loadingPackage || !orderSummary}
                  className="w-full mt-6 h-12 text-base font-semibold"
                  size="lg"
                >
                  {isSubmitting 
                    ? 'Processing...'
                    : loadingPackage
                    ? 'Loading...'
                    : 'Complete Booking'}
                </Button>
              </>
            ) : clientSecret && bookingId && orderSummary && stripePromise ? (
              <Elements stripe={stripePromise} options={elementsOptions}>
                <StripePaymentForm
                  clientSecret={clientSecret}
                  bookingId={bookingId}
                  orderSummary={orderSummary}
                  onSuccess={(paymentIntentId) => {
                    console.log('Payment succeeded:', paymentIntentId);
                  }}
                />
              </Elements>
            ) : orderSummary && orderSummary.total >= 0.50 ? (
              <>
                <Card className="mt-6">
                  <CardContent className="p-6 text-center">
                    <p className="text-muted-foreground mb-4">
                      Click "Complete Booking" to proceed with payment.
                    </p>
                  </CardContent>
                </Card>
                <Button
                  onClick={handleSubmit}
                  disabled={isSubmitting || loadingPackage || !orderSummary}
                  className="w-full mt-6 h-12 text-base font-semibold"
                  size="lg"
                >
                  {isSubmitting 
                    ? 'Processing...'
                    : loadingPackage
                    ? 'Loading...'
                    : 'Complete Booking'}
                </Button>
              </>
            ) : null}

            {/* Secure Payment Badge */}
            {orderSummary?.total > 0 && (
              <div className="mt-6 flex items-center justify-center gap-2 p-4 bg-muted/50 rounded-lg">
                <Shield className="h-4 w-4 text-primary" />
                <span className="text-sm font-medium">Secure Payment</span>
              </div>
            )}
      </div>
    </div>
  );
}
